import React, { useState, useEffect, useCallback } from "react";
import { View as RNView } from "react-native-web";
import CustomText from "../atoms/CustomText";
import { colors, spacing, borderRadius } from "../../utils/styling";
import { useAutoSaveExercise, ExerciseResponse } from "../../lib/profiles";

// Type-safe components
const View = RNView as unknown as React.FC<React.ComponentProps<typeof RNView>>;

interface Exercise {
  id: number;
  title: string;
  header: string;
  intro: string;
  prompt: string;
  tips: string[];
  footer: string;
}

interface ExerciseRendererProps {
  exercise: Exercise;
  onComplete?: (responses: any) => void;
  savedData?: ExerciseResponse; // Pre-populated data from backend
}

// Utility functions for parsing exercise prompts
const extractFieldsFromPrompt = (prompt: string) => {
  // Find all patterns like "Something: __________" and "Something __________"
  const patterns = [
    /([^.,!?]*?):\s*_+/g,  // Pattern with colon: "Something: ___"
    /([^.,!?]*?)\s+_+/g    // Pattern without colon: "Something ___"
  ];
  
  const allMatches: string[] = [];
  
  patterns.forEach(pattern => {
    const matches = prompt.match(pattern);
    if (matches) {
      allMatches.push(...matches);
    }
  });
  
  // Remove duplicates and clean up
  const uniqueMatches = Array.from(new Set(allMatches));
  
  return uniqueMatches.map((match, index) => {
    const label = match.replace(/[:\s]*_+.*/, '').trim();
    return {
      id: `field_${index}`,
      label: label || `Field ${index + 1}`,
      placeholder: 'Enter your response...'
    };
  });
};

const extractRankingOptions = (prompt: string) => {
  // Find text like "Options: Innovation, Growth, Autonomy" 
  const optionsMatch = prompt.match(/Options:\s*([^.]+)/);
  if (optionsMatch) {
    return optionsMatch[1].split(',').map(s => s.trim());
  }
  // Fallback for other formats
  return ['Option 1', 'Option 2', 'Option 3', 'Option 4', 'Option 5', 'Option 6'];
};

const extractMappingOptions = (prompt: string) => {
  // Use the same 5 options for all personality types
  return [
    'Debt-Free', 
    'Long-Term Planning', 
    'Enjoying Life', 
    'Giving Back', 
    'Growing Money'
  ];
};

// Exercise 1: Reflective - Simple multi-line input
const ReflectiveExercise: React.FC<{ exercise: Exercise; savedData?: ExerciseResponse; onAutoSave: (responses: any) => void }> = ({ exercise, savedData, onAutoSave }) => {
  const [response, setResponse] = useState(savedData?.responses?.reflection || '');

  // Auto-save when response changes
  useEffect(() => {
    if (response.trim()) {
      const timer = setTimeout(() => {
        onAutoSave({ reflection: response });
      }, 2000); // 2 second delay
      
      return () => clearTimeout(timer);
    }
  }, [response, onAutoSave]);

  return (
    <View style={{ gap: spacing.lg }}>
      <CustomText style={{
        fontSize: 16,
        color: colors.gray[700],
        lineHeight: 24,
        fontFamily: 'Nunito'
      }}>
        {exercise.prompt}
      </CustomText>
      
      <textarea
        value={response}
        onChange={(e) => setResponse(e.target.value)}
        placeholder="Share your thoughts and reflections here..."
        style={{
          width: '100%',
          minHeight: 120,
          padding: `${spacing.md}px ${spacing.lg}px`,
          border: `1px solid ${colors.gray[300]}`,
          borderRadius: borderRadius.md,
          fontSize: 14,
          fontFamily: 'Nunito',
          lineHeight: 1.5,
          resize: 'vertical',
          boxSizing: 'border-box'
        }}
      />
    </View>
  );
};

// Exercise 2: Mapping - Drag and drop with expandable inputs
const MappingExercise: React.FC<{ exercise: Exercise; savedData?: ExerciseResponse; onAutoSave: (responses: any) => void }> = ({ exercise, savedData, onAutoSave }) => {
  const initialOptions = extractMappingOptions(exercise.prompt);
  const [items, setItems] = useState(savedData?.responses?.priority_order || initialOptions);
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());
  const [responses, setResponses] = useState<Record<string, string>>(savedData?.responses?.thoughts || {});
  const [draggedItem, setDraggedItem] = useState<number | null>(null);

  const handleDragStart = (index: number) => {
    setDraggedItem(index);
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (draggedItem === null) return;

    const newItems = [...items];
    const draggedContent = newItems[draggedItem];
    newItems.splice(draggedItem, 1);
    newItems.splice(index, 0, draggedContent);
    
    setItems(newItems);
    setDraggedItem(index);
  };

  const handleDragEnd = () => {
    setDraggedItem(null);
  };

  const toggleExpanded = (item: string) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(item)) {
      newExpanded.delete(item);
    } else {
      newExpanded.add(item);
    }
    setExpandedItems(newExpanded);
  };

  const updateResponse = (item: string, value: string) => {
    setResponses(prev => ({ ...prev, [item]: value }));
  };

  // Auto-save when items order or responses change
  useEffect(() => {
    const timer = setTimeout(() => {
      onAutoSave({
        priority_order: items,
        thoughts: responses
      });
    }, 2000);
    
    return () => clearTimeout(timer);
  }, [items, responses, onAutoSave]);

  return (
    <View style={{ gap: spacing.lg }}>
      <CustomText style={{
        fontSize: 16,
        color: colors.gray[700],
        lineHeight: 24,
        fontFamily: 'Nunito'
      }}>
        {exercise.prompt}
      </CustomText>
      
      <CustomText style={{
        fontSize: 14,
        color: colors.gray[600],
        fontFamily: 'Nunito',
        fontStyle: 'italic'
      }}>
        Drag to reorder by importance, then click to expand and add your thoughts:
      </CustomText>

      <View style={{ display: 'flex', flexDirection: 'column', gap: spacing.sm }}>
        {items.map((item: string, index: number) => (
          <div
            key={item}
            draggable
            onDragStart={() => handleDragStart(index)}
            onDragOver={(e) => handleDragOver(e, index)}
            onDragEnd={handleDragEnd}
            style={{
              border: `1px solid ${colors.gray[300]}`,
              borderRadius: borderRadius.md,
              overflow: 'hidden',
              backgroundColor: draggedItem === index ? colors.gray[100] : 'white',
              transition: 'background-color 0.2s'
            }}
          >
            <button
              onClick={() => toggleExpanded(item)}
              style={{
                width: '100%',
                padding: spacing.md,
                backgroundColor: 'transparent',
                border: 'none',
                cursor: 'pointer',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}
            >
              <View style={{
                display: 'flex',
                alignItems: 'center',
                gap: spacing.md
              }}>
                <View style={{
                  width: 24,
                  height: 24,
                  borderRadius: 12,
                  backgroundColor: colors.primary,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <CustomText style={{
                    color: 'white',
                    fontSize: 12,
                    fontWeight: '700',
                    fontFamily: 'Nunito'
                  }}>
                    {index + 1}
                  </CustomText>
                </View>
                
                <CustomText style={{
                  fontSize: 16,
                  fontWeight: '600',
                  color: colors.dark,
                  fontFamily: 'Nunito'
                }}>
                  {item}
                </CustomText>
              </View>

              <View style={{ display: 'flex', alignItems: 'center', gap: spacing.sm }}>
                <CustomText style={{
                  fontSize: 18,
                  color: colors.gray[500],
                  transform: expandedItems.has(item) ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s'
                }}>
                  ▼
                </CustomText>
                <CustomText style={{
                  fontSize: 20,
                  color: colors.gray[400]
                }}>
                  ⋮⋮
                </CustomText>
              </View>
            </button>
            
            {expandedItems.has(item) && (
              <View style={{ padding: spacing.md, borderTop: `1px solid ${colors.gray[200]}` }}>
                <textarea
                  value={responses[item] || ''}
                  onChange={(e) => updateResponse(item, e.target.value)}
                  placeholder={`Why does ${item} matter to you?`}
                  style={{
                    width: '100%',
                    minHeight: 80,
                    padding: `${spacing.md}px ${spacing.lg}px`,
                    border: `1px solid ${colors.gray[300]}`,
                    borderRadius: borderRadius.sm,
                    fontSize: 14,
                    fontFamily: 'Nunito',
                    resize: 'vertical',
                    boxSizing: 'border-box'
                  }}
                />
              </View>
            )}
          </div>
        ))}
      </View>
    </View>
  );
};

// Exercise 3: Action Planning - Form with inputs for each blank
const ActionPlanningExercise: React.FC<{ exercise: Exercise; savedData?: ExerciseResponse; onAutoSave: (responses: any) => void }> = ({ exercise, savedData, onAutoSave }) => {
  const fields = extractFieldsFromPrompt(exercise.prompt);
  const [responses, setResponses] = useState<Record<string, string>>(savedData?.responses || {});

  const updateResponse = (fieldId: string, value: string) => {
    setResponses(prev => ({ ...prev, [fieldId]: value }));
  };

  // Auto-save when responses change
  useEffect(() => {
    if (Object.keys(responses).length > 0) {
      const timer = setTimeout(() => {
        onAutoSave(responses);
      }, 2000);
      
      return () => clearTimeout(timer);
    }
  }, [responses, onAutoSave]);

  return (
    <View style={{ gap: spacing.lg }}>
      <CustomText style={{
        fontSize: 16,
        color: colors.gray[700],
        lineHeight: 24,
        fontFamily: 'Nunito'
      }}>
        Fill in each field below:
      </CustomText>

      <View style={{ display: 'flex', flexDirection: 'column', gap: spacing.md }}>
        {fields.map((field) => (
          <View key={field.id} style={{ display: 'flex', flexDirection: 'column', gap: spacing.xs }}>
            <CustomText style={{
              fontSize: 14,
              fontWeight: '600',
              color: colors.dark,
              fontFamily: 'Nunito'
            }}>
              {field.label}
            </CustomText>
            <input
              type="text"
              value={responses[field.id] || ''}
              onChange={(e) => updateResponse(field.id, e.target.value)}
              placeholder={field.placeholder}
              style={{
                padding: `${spacing.md}px ${spacing.lg}px`,
                border: `1px solid ${colors.gray[300]}`,
                borderRadius: borderRadius.md,
                fontSize: 14,
                fontFamily: 'Nunito',
                boxSizing: 'border-box'
              }}
            />
          </View>
        ))}
      </View>

      {/* Show original prompt for reference */}
      <View style={{
        padding: spacing.md,
        backgroundColor: colors.gray[100],
        borderRadius: borderRadius.md,
        border: `1px solid ${colors.gray[200]}`
      }}>
        <CustomText style={{
          fontSize: 12,
          fontWeight: '600',
          color: colors.gray[600],
          marginBottom: spacing.xs,
          fontFamily: 'Nunito'
        }}>
          Original Prompt:
        </CustomText>
        <CustomText style={{
          fontSize: 14,
          color: colors.gray[600],
          fontFamily: 'Nunito',
          fontStyle: 'italic'
        }}>
          {exercise.prompt}
        </CustomText>
      </View>
    </View>
  );
};

// Exercise 4: Tracking - Three day inputs
const TrackingExercise: React.FC<{ exercise: Exercise; savedData?: ExerciseResponse; onAutoSave: (responses: any) => void }> = ({ exercise, savedData, onAutoSave }) => {
  const [responses, setResponses] = useState({
    day1: savedData?.responses?.day1 || '',
    day2: savedData?.responses?.day2 || '',
    day3: savedData?.responses?.day3 || ''
  });

  const updateDay = (day: 'day1' | 'day2' | 'day3', value: string) => {
    setResponses(prev => ({ ...prev, [day]: value }));
  };

  // Auto-save when responses change
  useEffect(() => {
    if (responses.day1 || responses.day2 || responses.day3) {
      const timer = setTimeout(() => {
        onAutoSave(responses);
      }, 2000);
      
      return () => clearTimeout(timer);
    }
  }, [responses, onAutoSave]);

  return (
    <View style={{ gap: spacing.lg }}>
      <CustomText style={{
        fontSize: 16,
        color: colors.gray[700],
        lineHeight: 24,
        fontFamily: 'Nunito'
      }}>
        {exercise.prompt}
      </CustomText>

      <View style={{ display: 'flex', flexDirection: 'column', gap: spacing.lg }}>
        {[
          { key: 'day1' as const, label: 'Day 1' },
          { key: 'day2' as const, label: 'Day 2' },
          { key: 'day3' as const, label: 'Day 3' }
        ].map(({ key, label }) => (
          <View key={key} style={{ display: 'flex', flexDirection: 'column', gap: spacing.sm }}>
            <CustomText style={{
              fontSize: 16,
              fontWeight: '700',
              color: colors.dark,
              fontFamily: 'Nunito'
            }}>
              {label}
            </CustomText>
            <textarea
              value={responses[key]}
              onChange={(e) => updateDay(key, e.target.value)}
              placeholder={`${label} reflection...`}
                          style={{
              width: '100%',
              minHeight: 100,
              padding: `${spacing.md}px ${spacing.lg}px`,
              border: `1px solid ${colors.gray[300]}`,
              borderRadius: borderRadius.md,
              fontSize: 14,
              fontFamily: 'Nunito',
              lineHeight: 1.5,
              resize: 'vertical',
              boxSizing: 'border-box'
            }}
            />
          </View>
        ))}
      </View>
    </View>
  );
};

// Exercise 5: Ranking - Drag to reorder priorities
const RankingExercise: React.FC<{ exercise: Exercise; savedData?: ExerciseResponse; onAutoSave: (responses: any) => void }> = ({ exercise, savedData, onAutoSave }) => {
  const initialOptions = extractRankingOptions(exercise.prompt);
  const [rankedItems, setRankedItems] = useState(savedData?.responses?.ranked_priorities || initialOptions);
  const [draggedItem, setDraggedItem] = useState<number | null>(null);

  const handleDragStart = (index: number) => {
    setDraggedItem(index);
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (draggedItem === null) return;

    const newItems = [...rankedItems];
    const draggedContent = newItems[draggedItem];
    newItems.splice(draggedItem, 1);
    newItems.splice(index, 0, draggedContent);
    
    setRankedItems(newItems);
    setDraggedItem(index);
  };

  const handleDragEnd = () => {
    setDraggedItem(null);
  };

  // Auto-save when ranked items change
  useEffect(() => {
    const timer = setTimeout(() => {
      onAutoSave({ ranked_priorities: rankedItems });
    }, 2000);
    
    return () => clearTimeout(timer);
  }, [rankedItems, onAutoSave]);

  return (
    <View style={{ gap: spacing.lg }}>
      <CustomText style={{
        fontSize: 16,
        color: colors.gray[700],
        lineHeight: 24,
        fontFamily: 'Nunito'
      }}>
        {exercise.prompt.split('Options:')[0]}
      </CustomText>
      
      <CustomText style={{
        fontSize: 14,
        color: colors.gray[600],
        fontFamily: 'Nunito',
        fontStyle: 'italic'
      }}>
        Drag and drop to rank in order of importance (1 = most important):
      </CustomText>

      <View style={{ display: 'flex', flexDirection: 'column', gap: spacing.sm }}>
        {rankedItems.map((item: string, index: number) => (
          <div
            key={item}
            draggable
            onDragStart={() => handleDragStart(index)}
            onDragOver={(e) => handleDragOver(e, index)}
            onDragEnd={handleDragEnd}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: spacing.md,
              padding: spacing.md,
              backgroundColor: draggedItem === index ? colors.gray[100] : 'white',
              border: `1px solid ${colors.gray[300]}`,
              borderRadius: borderRadius.md,
              cursor: 'grab',
              transition: 'background-color 0.2s'
            }}
          >
            <View style={{
              width: 24,
              height: 24,
              borderRadius: 12,
              backgroundColor: colors.primary,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <CustomText style={{
                color: 'white',
                fontSize: 12,
                fontWeight: '700',
                fontFamily: 'Nunito'
              }}>
                {index + 1}
              </CustomText>
            </View>
            
            <CustomText style={{
              fontSize: 16,
              color: colors.dark,
              fontFamily: 'Nunito',
              flex: 1
            }}>
              {item}
            </CustomText>
            
            <CustomText style={{
              fontSize: 20,
              color: colors.gray[400]
            }}>
              ⋮⋮
            </CustomText>
          </div>
        ))}
      </View>
    </View>
  );
};

// Main Exercise Renderer Component
const ExerciseRenderer: React.FC<ExerciseRendererProps> = ({ exercise, onComplete, savedData }) => {
  const autoSaveMutation = useAutoSaveExercise();

  const handleAutoSave = useCallback((responses: any) => {
    autoSaveMutation.mutate({
      exercise_id: exercise.id,
      title: exercise.title,
      responses: responses
    });
  }, [exercise.id, exercise.title, autoSaveMutation]);

  const renderExercise = () => {
    switch (exercise.id) {
      case 1:
        return <ReflectiveExercise exercise={exercise} savedData={savedData} onAutoSave={handleAutoSave} />;
      case 2:
        return <MappingExercise exercise={exercise} savedData={savedData} onAutoSave={handleAutoSave} />;
      case 3:
        return <ActionPlanningExercise exercise={exercise} savedData={savedData} onAutoSave={handleAutoSave} />;
      case 4:
        return <TrackingExercise exercise={exercise} savedData={savedData} onAutoSave={handleAutoSave} />;
      case 5:
        return <RankingExercise exercise={exercise} savedData={savedData} onAutoSave={handleAutoSave} />;
      default:
        return <ReflectiveExercise exercise={exercise} savedData={savedData} onAutoSave={handleAutoSave} />;
    }
  };

  return (
    <View style={{ display: 'flex', flexDirection: 'column', gap: spacing.xl }}>
      {/* Exercise Header */}
      <View style={{ display: 'flex', flexDirection: 'column', gap: spacing.md }}>
        <CustomText style={{
          fontSize: 24,
          fontWeight: '700',
          color: colors.dark,
          fontFamily: 'Nunito'
        }}>
          {exercise.header}
        </CustomText>
        
        <CustomText style={{
          fontSize: 16,
          color: colors.gray[600],
          lineHeight: 24,
          fontFamily: 'Nunito'
        }}>
          {exercise.intro}
        </CustomText>
      </View>

      {/* Interactive Exercise Component */}
      <View style={{
        padding: spacing.xl,
        backgroundColor: colors.gray[100],
        borderRadius: borderRadius.lg,
        border: `1px solid ${colors.gray[200]}`
      }}>
        {renderExercise()}
      </View>

      {/* Tips Section */}
      {exercise.tips.length > 0 && (
        <View style={{
          padding: spacing.lg,
          backgroundColor: colors.primary + '10',
          borderRadius: borderRadius.md,
          border: `1px solid ${colors.primary}30`
        }}>
          <CustomText style={{
            fontSize: 14,
            fontWeight: '600',
            color: colors.primary,
            marginBottom: spacing.sm,
            fontFamily: 'Nunito'
          }}>
            Tips:
          </CustomText>
          {exercise.tips.map((tip, index) => (
            <CustomText key={index} style={{
              fontSize: 14,
              color: colors.gray[700],
              marginBottom: spacing.xs,
              fontFamily: 'Nunito'
            }}>
              • {tip}
            </CustomText>
          ))}
        </View>
      )}

      {/* Footer */}
      {exercise.footer && (
        <CustomText style={{
          fontSize: 16,
          color: colors.gray[700],
          fontStyle: 'italic',
          textAlign: 'center',
          fontFamily: 'Nunito'
        }}>
          {exercise.footer}
        </CustomText>
      )}
    </View>
  );
};

export default ExerciseRenderer;